// frontend/src/components/placements/PlacementOutreachPage.jsx
import React, { useState, useEffect, useContext } from "react";
import { useSearchParams } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";
import { formatPhone } from "../../utils/formatPhone";
import { formatDate } from "../../utils/dateFormatter";

const PlacementOutreachPage = () => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();
  const [searchParams, setSearchParams] = useSearchParams();

  const [followups, setFollowups] = useState([]);
  const [stages, setStages] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [consumers, setConsumers] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(null);

  // Employer modal state
  const [showEmployerModal, setShowEmployerModal] = useState(false);
  const [savingEmployer, setSavingEmployer] = useState(false);
  const [employerForm, setEmployerForm] = useState({
    company_name: "",
    contact_name: "",
    contact_title: "",
    phone: "",
    email: "",
    industry: "",
  });

  const [formData, setFormData] = useState({
    employer_id: "",
    consumer_id: "",
    stage_id: "",
    followup_date: new Date().toISOString().split("T")[0],
    contact_method: "phone",
    contacted_person: "",
    outcome: "",
    next_steps: "",
    next_followup_date: "",
    status: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    employer_id: "",
    consumer_id: "",
    stage_id: "",
  });

  const loadData = async () => {
    if (!user?.tenant_id) return;

    setLoading(true);
    try {
      const [followupsRes, stagesRes, employersRes, consumersRes, statesRes] =
        await Promise.all([
          secureAxios.get(`${apiBase}/api/placement-outreach`, {
            params: { tenant_id: user.tenant_id, ...filters },
          }),
          secureAxios.get(`${apiBase}/api/followup-stages`, {
            params: { tenant_id: user.tenant_id },
          }),
          secureAxios.get(`${apiBase}/api/employers/dropdown`, {
            params: { tenant_id: user.tenant_id },
          }),
          secureAxios.get(`${apiBase}/api/consumers/dropdown`, {
            params: { tenant_id: user.tenant_id },
          }),
          secureAxios.get(`${apiBase}/api/states`),
        ]);

      setFollowups(followupsRes.data);
      setStages(stagesRes.data.filter((s) => s.is_active));
      setEmployers(employersRes.data);
      setConsumers(consumersRes.data);
      setStates(statesRes.data);
    } catch (err) {
      console.error("Error loading data:", err);
      toast.error("Failed to load outreach data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [user?.tenant_id, filters]);

  // Check for action=new query param to auto-open the form
  useEffect(() => {
    if (searchParams.get("action") === "new") {
      setShowForm(true);
      setEditingFollowup(null);
      
      // Pre-select consumer if consumer_id is provided
      const consumerId = searchParams.get("consumer_id");
      if (consumerId) {
        setFormData(prev => ({
          ...prev,
          consumer_id: consumerId,
        }));
      }
      
      // Clear the query params so it doesn't re-trigger
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  const resetForm = () => {
    setFormData({
      employer_id: "",
      consumer_id: "",
      stage_id: "",
      followup_date: new Date().toISOString().split("T")[0],
      contact_method: "phone",
      contacted_person: "",
      outcome: "",
      next_steps: "",
      next_followup_date: "",
      status: "",
    });
    setEditingFollowup(null);
    setError("");
  };

  const handleCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = (followup) => {
    setEditingFollowup(followup);
    setFormData({
      employer_id: followup.employer_id || "",
      consumer_id: followup.consumer_id || "",
      stage_id: followup.stage_id || "",
      followup_date: followup.followup_date
        ? followup.followup_date.split("T")[0]
        : "",
      contact_method: followup.contact_method || "phone",
      contacted_person: followup.contacted_person || "",
      outcome: followup.outcome || "",
      next_steps: followup.next_steps || "",
      next_followup_date: followup.next_followup_date
        ? followup.next_followup_date.split("T")[0]
        : "",
      status: followup.status || "completed",
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    resetForm();
  };

  // Calculate next follow-up date based on current stage and follow-up date
  const calculateNextFollowupDate = (stageId, followupDate) => {
    if (!stageId || !followupDate || stages.length === 0) return "";
    
    // Find current stage and its order
    const currentStage = stages.find(s => s.stage_id === stageId);
    if (!currentStage) return "";
    
    // Find next stage (by sort_order)
    const sortedStages = [...stages].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    const currentIndex = sortedStages.findIndex(s => s.stage_id === stageId);
    
    // If there's a next stage, use its days_after_previous
    if (currentIndex >= 0 && currentIndex < sortedStages.length - 1) {
      const nextStage = sortedStages[currentIndex + 1];
      if (nextStage.days_after_previous) {
        const date = new Date(followupDate);
        date.setDate(date.getDate() + parseInt(nextStage.days_after_previous));
        return date.toISOString().split("T")[0];
      }
    }
    
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: value,
      };
      
      // Auto-calculate next follow-up date when stage or follow-up date changes
      if (name === "stage_id" || name === "followup_date") {
        const stageId = name === "stage_id" ? value : prev.stage_id;
        const followupDate = name === "followup_date" ? value : prev.followup_date;
        
        if (stageId && followupDate) {
          const calculatedDate = calculateNextFollowupDate(stageId, followupDate);
          if (calculatedDate) {
            updated.next_followup_date = calculatedDate;
          }
        }
      }
      
      return updated;
    });
    
    if (error) setError("");
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.employer_id) {
      setError("Employer is required");
      return;
    }
    if (!formData.followup_date) {
      setError("Follow-up date is required");
      return;
    }

    setSaving(true);
    setError("");

    const payload = {
      ...formData,
      tenant_id: user.tenant_id,
      consumer_id: formData.consumer_id || null,
      stage_id: formData.stage_id || null,
      next_followup_date: formData.next_followup_date || null,
      [editingFollowup ? "updated_by" : "created_by"]: user.user_id,
    };

    try {
      if (editingFollowup) {
        const res = await secureAxios.put(
          `${apiBase}/api/placement-outreach/${editingFollowup.followup_id}`,
          payload
        );
        toast.success("Outreach record updated successfully");
      } else {
        const res = await secureAxios.post(
          `${apiBase}/api/placement-outreach`,
          payload
        );
        if (res.data?.job_search_contact_id) {
          toast.success("Outreach created and synced to CRP-33!");
        } else {
          toast.success("Outreach record created successfully");
        }
      }

      setShowForm(false);
      resetForm();
      loadData();
    } catch (err) {
      console.error("Error saving outreach:", err);
      setError(err.response?.data?.error || "Failed to save outreach record");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (followup) => {
    if (
      !window.confirm("Are you sure you want to delete this outreach record?")
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `${apiBase}/api/placement-outreach/${followup.followup_id}`,
        { params: { tenant_id: user.tenant_id } }
      );
      toast.success("Outreach record deleted successfully");
      loadData();
    } catch (err) {
      console.error("Error deleting outreach:", err);
      toast.error(
        err.response?.data?.error || "Failed to delete outreach record"
      );
    }
  };

  // ============ Employer Modal Handlers ============
  const industries = [
    "Agriculture",
    "Construction",
    "Education",
    "Finance",
    "Food Service",
    "Healthcare",
    "Hospitality",
    "Information Technology",
    "Manufacturing",
    "Non-Profit",
    "Retail",
    "Transportation",
    "Utilities",
    "Warehouse/Logistics",
    "Other",
  ];

  const resetEmployerForm = () => {
    setEmployerForm({
      company_name: "",
      contact_name: "",
      contact_title: "",
      phone: "",
      email: "",
      industry: "",
    });
  };

  const handleEmployerFormChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "phone") {
      newValue = formatPhone(value);
    }
    setEmployerForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleCreateEmployer = async (e) => {
    e.preventDefault();

    if (!employerForm.company_name.trim()) {
      toast.error("Company name is required");
      return;
    }

    setSavingEmployer(true);
    try {
      const payload = {
        ...employerForm,
        phone: employerForm.phone.replace(/\D/g, ""),
        tenant_id: user.tenant_id,
        created_by: user.user_id,
        is_active: true,
      };

      const res = await secureAxios.post(`${apiBase}/api/employers`, payload);
      toast.success("Employer created successfully");

      // Refresh employers list
      const employersRes = await secureAxios.get(
        `${apiBase}/api/employers/dropdown`,
        { params: { tenant_id: user.tenant_id } }
      );
      setEmployers(employersRes.data);

      // Select the newly created employer in the form
      if (res.data.employer_id) {
        setFormData((prev) => ({
          ...prev,
          employer_id: res.data.employer_id,
        }));
      }

      setShowEmployerModal(false);
      resetEmployerForm();
    } catch (err) {
      console.error("Error creating employer:", err);
      toast.error(err.response?.data?.error || "Failed to create employer");
    } finally {
      setSavingEmployer(false);
    }
  };

  const contactMethods = [
    { value: "phone", label: "Phone" },
    { value: "email", label: "Email" },
    { value: "in-person", label: "In-Person" },
    { value: "other", label: "Other" },
  ];

  const statusOptions = [
    { value: "completed", label: "Completed" },
    { value: "attempted", label: "Attempted" },
    { value: "scheduled", label: "Scheduled" },
    { value: "pending", label: "Pending" },
    { value: "no_answer", label: "No Answer" },
    { value: "left_message", label: "Left Message" },
    { value: "callback_requested", label: "Callback Requested" },
    { value: "not_interested", label: "Not Interested" },
    { value: "follow_up_needed", label: "Follow-up Needed" },
  ];

  const getStatusBadge = (status) => {
    const badges = {
      completed: { class: "bg-success", label: "Completed" },
      attempted: { class: "bg-warning text-dark", label: "Attempted" },
      scheduled: { class: "bg-info", label: "Scheduled" },
      pending: { class: "bg-secondary", label: "Pending" },
      no_answer: { class: "bg-danger", label: "No Answer" },
      left_message: { class: "bg-primary", label: "Left Message" },
      callback_requested: { class: "bg-warning text-dark", label: "Callback Requested" },
      not_interested: { class: "bg-dark", label: "Not Interested" },
      follow_up_needed: { class: "bg-info", label: "Follow-up Needed" },
    };
    const badge = badges[status] || badges.completed;
    return <span className={`badge ${badge.class}`}>{badge.label}</span>;
  };

  return (
    <div className="container my-4">
    <div className="container-fluid px-2 px-md-4 mt-3 mt-md-4">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-2 mb-md-3 gap-2">
        <h4 className="mb-0 h5">
          <i className="bi bi-telephone-outbound me-2"></i>
          Outreach
        </h4>
        {!showForm && (
          <button className="btn btn-primary" onClick={handleCreate}>
            <i className="bi bi-plus-circle me-2"></i>
            Log Outreach
          </button>
        )}
      </div>

      <div className="alert alert-info mb-2 mb-md-3 py-2 small">
        <i className="bi bi-info-circle me-2"></i>
        <strong>Pre-Placement Outreach:</strong> Track your follow-up activities
        with employers as you work to secure job placements for consumers.
      </div>

      {showForm ? (
        <div className="card">
          <div className="card-header bg-primary text-white py-2">
            <i className="bi bi-pencil me-2"></i>
            {editingFollowup ? "Edit Outreach Record" : "Log New Outreach"}
          </div>
          <div className="card-body p-2 p-md-3">
            {error && (
              <div className="alert alert-danger alert-dismissible">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError("")}
                ></button>
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row g-2 g-md-3">
                <div className="col-md-6">
                  <label className="form-label">
                    Employer <span className="text-danger">*</span>
                  </label>
                  <div className="input-group input-group-sm">
                    <select
                      name="employer_id"
                      className="form-select form-select-sm"
                      value={formData.employer_id}
                      onChange={handleChange}
                      required
                      disabled={saving}
                    >
                      <option value="">Select Employer</option>
                      {employers.map((emp) => (
                        <option key={emp.employer_id} value={emp.employer_id}>
                          {emp.company_name}
                          {emp.contact_name ? ` (${emp.contact_name})` : ""}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-success"
                      onClick={() => setShowEmployerModal(true)}
                      disabled={saving}
                      title="Create New Employer"
                    >
                      <i className="bi bi-plus-lg"></i>
                    </button>
                  </div>
                  <small className="text-muted">
                    <i className="bi bi-file-earmark-text me-1"></i>
                    CRP-33: "Employer"
                  </small>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Consumer (Optional)</label>
                  <select
                    name="consumer_id"
                    className="form-select form-select-sm"
                    value={formData.consumer_id}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    <option value="">
                      General Outreach (No Specific Consumer)
                    </option>
                    {consumers.map((consumer) => (
                      <option
                        key={consumer.consumer_id}
                        value={consumer.consumer_id}
                      >
                        {consumer.name}
                      </option>
                    ))}
                  </select>
                  <small className="text-muted">
                    <i className="bi bi-file-earmark-text me-1"></i>
                    CRP-33: "Consumer" (shows as "Unknown" if not selected)
                  </small>
                  {formData.employer_id && (
                    <small className="text-success mt-1 d-block">
                      <i className="bi bi-link-45deg me-1"></i>
                      Will sync to CRP-33 Job Search Data Sheet
                    </small>
                  )}
                </div>

                <div className="col-md-4">
                  <label className="form-label">Stage</label>
                  <select
                    name="stage_id"
                    className="form-select form-select-sm"
                    value={formData.stage_id}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    <option value="">No Stage Selected</option>
                    {stages.map((stage) => (
                      <option key={stage.stage_id} value={stage.stage_id}>
                        {stage.stage_order}. {stage.stage_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">
                    Follow-up Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    name="followup_date"
                    className="form-control form-control-sm"
                    value={formData.followup_date}
                    onChange={handleChange}
                    required
                    disabled={saving}
                  />
                  <small className="text-muted">
                    <i className="bi bi-file-earmark-text me-1"></i>
                    CRP-33: "Date"
                  </small>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Contact Method</label>
                  <select
                    name="contact_method"
                    className="form-select form-select-sm"
                    value={formData.contact_method}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    {contactMethods.map((method) => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Contacted Person</label>
                  <input
                    type="text"
                    name="contacted_person"
                    className="form-control form-control-sm"
                    value={formData.contacted_person}
                    onChange={handleChange}
                    placeholder="Name of person you spoke with"
                    disabled={saving}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label">Status</label>
                  <select
                    name="status"
                    className="form-select form-select-sm"
                    value={formData.status}
                    onChange={handleChange}
                    disabled={saving}
                  >
                    <option value="">Select Status...</option>
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-12">
                  <label className="form-label">Outcome</label>
                  <textarea
                    name="outcome"
                    className="form-control form-control-sm"
                    value={formData.outcome}
                    onChange={handleChange}
                    rows="2"
                    placeholder="What happened during this contact?"
                    disabled={saving}
                  />
                  <small className="text-muted">
                    <i className="bi bi-file-earmark-text me-1"></i>
                    CRP-33: "Result of Contact"
                  </small>
                </div>

                <div className="col-12">
                  <label className="form-label">Next Steps</label>
                  <textarea
                    name="next_steps"
                    className="form-control form-control-sm"
                    value={formData.next_steps}
                    onChange={handleChange}
                    rows="2"
                    placeholder="What actions need to be taken next?"
                    disabled={saving}
                  />
                  <small className="text-muted">
                    <i className="bi bi-file-earmark-text me-1"></i>
                    CRP-33: "Comments"
                  </small>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Next Follow-up Date</label>
                  <input
                    type="date"
                    name="next_followup_date"
                    className="form-control form-control-sm"
                    value={formData.next_followup_date}
                    onChange={handleChange}
                    disabled={saving}
                  />
                  {formData.stage_id && formData.next_followup_date && (() => {
                    const currentStage = stages.find(s => s.stage_id === formData.stage_id);
                    if (!currentStage) return null;
                    const sortedStages = [...stages].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
                    const currentIndex = sortedStages.findIndex(s => s.stage_id === formData.stage_id);
                    if (currentIndex >= 0 && currentIndex < sortedStages.length - 1) {
                      const nextStage = sortedStages[currentIndex + 1];
                      if (nextStage.days_after_previous) {
                        return (
                          <small className="text-success">
                            <i className="bi bi-calculator me-1"></i>
                            Based on next stage: "{nextStage.stage_name}" (+{nextStage.days_after_previous} days)
                          </small>
                        );
                      }
                    }
                    return null;
                  })()}
                </div>
              </div>

              <div className="mt-4">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      {editingFollowup ? "Update Record" : "Log Outreach"}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : (
        <>
          <div className="row g-2 mb-2 mb-md-3">
            <div className="col-md-3">
              <select
                name="employer_id"
                className="form-select form-select-sm"
                value={filters.employer_id}
                onChange={handleFilterChange}
              >
                <option value="">All Employers</option>
                {employers.map((emp) => (
                  <option key={emp.employer_id} value={emp.employer_id}>
                    {emp.company_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select
                name="consumer_id"
                className="form-select form-select-sm"
                value={filters.consumer_id}
                onChange={handleFilterChange}
              >
                <option value="">All Consumers</option>
                {consumers.map((consumer) => (
                  <option
                    key={consumer.consumer_id}
                    value={consumer.consumer_id}
                  >
                    {consumer.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <select
                name="stage_id"
                className="form-select form-select-sm"
                value={filters.stage_id}
                onChange={handleFilterChange}
              >
                <option value="">All Stages</option>
                {stages.map((stage) => (
                  <option key={stage.stage_id} value={stage.stage_id}>
                    {stage.stage_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-3 text-end">
              <button
                className="btn btn-outline-secondary"
                onClick={() =>
                  setFilters({ employer_id: "", consumer_id: "", stage_id: "" })
                }
              >
                Clear Filters
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : followups.length === 0 ? (
            <div className="text-center py-5">
              <i
                className="bi bi-telephone-outbound text-muted"
                style={{ fontSize: "3rem" }}
              ></i>
              <h5 className="mt-3 text-muted">No Outreach Records Found</h5>
              <p className="text-muted">
                {Object.values(filters).some((f) => f)
                  ? "Try adjusting your filters"
                  : "Log your first outreach activity"}
              </p>
              {!Object.values(filters).some((f) => f) && (
                <button className="btn btn-primary" onClick={handleCreate}>
                  <i className="bi bi-plus-circle me-2"></i>
                  Log Outreach
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table View */}
              <div className="table-responsive d-none d-lg-block">
                <table className="table table-hover table-sm">
                  <thead className="table-light">
                    <tr>
                      <th>Date</th>
                      <th>Employer</th>
                      <th>Consumer</th>
                      <th>Stage</th>
                      <th>Method</th>
                      <th>Status</th>
                      <th>Next Follow-up</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followups.map((followup) => (
                      <tr key={followup.followup_id}>
                        <td>{formatDate(followup.followup_date)}</td>
                        <td>
                          <strong>{followup.company_name}</strong>
                        </td>
                        <td>{followup.consumer_name || "-"}</td>
                        <td>
                          {followup.stage_name ? (
                            <span className="badge bg-secondary">
                              {followup.stage_name}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td className="text-capitalize">
                          {followup.contact_method}
                        </td>
                        <td>{getStatusBadge(followup.status)}</td>
                        <td>
                          {followup.next_followup_date ? (
                            <span
                              className={
                                new Date(followup.next_followup_date) < new Date()
                                  ? "text-danger"
                                  : ""
                              }
                            >
                              {formatDate(followup.next_followup_date)}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            <button
                              className="btn btn-outline-primary"
                              onClick={() => handleEdit(followup)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDelete(followup)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="d-block d-lg-none">
                {followups.map((followup) => (
                  <div key={followup.followup_id} className="card mb-2">
                    <div className="card-body p-2">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          <div className="fw-bold text-truncate">
                            {followup.company_name}
                          </div>
                          <div className="small text-muted">
                            {followup.consumer_name || "No consumer"}
                          </div>
                        </div>
                        <div className="ms-2 text-end">
                          {getStatusBadge(followup.status)}
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-2 small text-muted mb-2">
                        <span>
                          <i className="bi bi-calendar me-1"></i>
                          {formatDate(followup.followup_date)}
                        </span>
                        {followup.stage_name && (
                          <span className="badge bg-secondary">
                            {followup.stage_name}
                          </span>
                        )}
                        <span className="text-capitalize">
                          <i className="bi bi-telephone me-1"></i>
                          {followup.contact_method}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          {followup.next_followup_date ? (
                            <span className={new Date(followup.next_followup_date) < new Date() ? "text-danger" : ""}>
                              <i className="bi bi-arrow-right me-1"></i>
                              Next: {formatDate(followup.next_followup_date)}
                            </span>
                          ) : (
                            "No follow-up scheduled"
                          )}
                        </small>
                        <div className="btn-group btn-group-sm">
                          <button
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEdit(followup)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDelete(followup)}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {!loading && followups.length > 0 && (
            <div className="text-muted small">
              Showing {followups.length} outreach record
              {followups.length !== 1 ? "s" : ""}
            </div>
          )}
        </>
      )}

      {/* Create Employer Modal */}
      {showEmployerModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-success text-white">
                <h5 className="modal-title">
                  <i className="bi bi-building-add me-2"></i>
                  Create New Employer
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowEmployerModal(false);
                    resetEmployerForm();
                  }}
                  disabled={savingEmployer}
                ></button>
              </div>
              <form onSubmit={handleCreateEmployer}>
                <div className="modal-body">
                  <div className="row g-2 g-md-3">
                    <div className="col-md-6">
                      <label className="form-label">
                        Company Name <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        name="company_name"
                        className="form-control form-control-sm"
                        value={employerForm.company_name}
                        onChange={handleEmployerFormChange}
                        required
                        disabled={savingEmployer}
                        autoFocus
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Industry</label>
                      <select
                        name="industry"
                        className="form-select form-select-sm"
                        value={employerForm.industry}
                        onChange={handleEmployerFormChange}
                        disabled={savingEmployer}
                      >
                        <option value="">Select Industry</option>
                        {industries.map((ind) => (
                          <option key={ind} value={ind}>
                            {ind}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Contact Name</label>
                      <input
                        type="text"
                        name="contact_name"
                        className="form-control form-control-sm"
                        value={employerForm.contact_name}
                        onChange={handleEmployerFormChange}
                        disabled={savingEmployer}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Contact Title</label>
                      <input
                        type="text"
                        name="contact_title"
                        className="form-control form-control-sm"
                        value={employerForm.contact_title}
                        onChange={handleEmployerFormChange}
                        placeholder="e.g., HR Manager"
                        disabled={savingEmployer}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Phone</label>
                      <input
                        type="tel"
                        name="phone"
                        className="form-control form-control-sm"
                        value={employerForm.phone}
                        onChange={handleEmployerFormChange}
                        placeholder="(xxx) xxx-xxxx"
                        disabled={savingEmployer}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control form-control-sm"
                        value={employerForm.email}
                        onChange={handleEmployerFormChange}
                        disabled={savingEmployer}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="bi bi-info-circle me-1"></i>
                      You can add more details (address, notes, etc.) later from
                      the Employers page.
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEmployerModal(false);
                      resetEmployerForm();
                    }}
                    disabled={savingEmployer}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-success"
                    disabled={savingEmployer}
                  >
                    {savingEmployer ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Create Employer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default PlacementOutreachPage;